import type { QueryHost } from "./query-host.js";
import { collectMissingProps } from "./task-property-utils.js";
import { isHeavyGateActive } from "../runner/heavy-gate.js";
import {
  ToolLoopTracker,
  buildRepeatLoopChatMessage,
  buildRepeatLoopMessage,
  fingerprintToolCall,
} from "./tool-loop-tracker.js";

const PM_PLAN_FILE_TOOLS = new Set(["Write", "Edit", "MultiEdit"]);

const DESTRUCTIVE_PATTERNS: { name: string; re: RegExp }[] = [
  {
    name: "git push --force (without --force-with-lease)",
    re: /git\s+push\s+(?:-f\b|--force(?!-with-lease))/,
  },
  { name: "git push --delete", re: /git\s+push\s+(?:-d\b|--delete\b)/ },
  { name: "git reset --hard", re: /git\s+reset\s+--hard\b/ },
  {
    name: "rm -rf /",
    re: /rm\s+(?:-[a-zA-Z]*r[a-zA-Z]*f|-[a-zA-Z]*f[a-zA-Z]*r|--recursive\s+--force)\s+\/(?!\S)/,
  },
  { name: "sudo rm", re: /\bsudo\s+rm\b/ },
  { name: "chmod world-writable", re: /\bchmod\s+(?:-R\s+)?[0-7]*7{2,3}\b/ },
  { name: "dd to device", re: /\bdd\s+.*\bof=\/dev\// },
  { name: "redirect to block device", re: />\s*\/dev\/(?:sd[a-z]|nvme\d|xvd[a-z])/ },
  { name: "mkfs filesystem creation", re: /\bmkfs(?:\.|\s)/ },
  { name: "shutdown/poweroff/halt/reboot", re: /\b(?:shutdown|poweroff|halt|reboot)\b/ },
  { name: "fork bomb", re: /:\(\)\s*\{\s*:\|\s*:&\s*\}\s*;\s*:/ },
];

function matchesDestructive(cmd: string): string | null {
  for (const { name, re } of DESTRUCTIVE_PATTERNS) {
    if (re.test(cmd)) return name;
  }
  return null;
}

type ToolResult =
  | { behavior: "allow"; updatedInput?: Record<string, unknown> }
  | { behavior: "deny"; message: string };

function isPlanFile(input: Record<string, unknown>): boolean {
  const filePath = String(input.file_path ?? input.path ?? "");
  return filePath.includes(".claude/plans/");
}

// Read-only modes (discovery/help, and residual pre-exit auto) run their shell
// commands WITHOUT an approval dialog — the pod is the sandbox, and a planning
// agent that stops to ask has nobody at the terminal to answer. What stays
// blocked is the narrow set of commands that would turn planning into
// building: anything that writes history, moves the checkout, or publishes.
// Read-only investigation (git log/diff/status, DB queries, test runs) is free.
const READ_ONLY_BLOCKED_BASH: { name: string; re: RegExp }[] = [
  { name: "git commit", re: /\bgit\s+(?:-\S+\s+)*commit\b/ },
  { name: "git push", re: /\bgit\s+(?:-\S+\s+)*push\b/ },
  { name: "git merge", re: /\bgit\s+(?:-\S+\s+)*merge\b/ },
  { name: "git rebase", re: /\bgit\s+(?:-\S+\s+)*rebase\b/ },
  { name: "git checkout/switch", re: /\bgit\s+(?:-\S+\s+)*(?:checkout|switch)\b/ },
  { name: "git apply/am", re: /\bgit\s+(?:-\S+\s+)*(?:apply|am)\b/ },
  { name: "gh pr create", re: /\bgh\s+pr\s+(?:create|merge)\b/ },
  { name: "package publish", re: /\b(?:npm|bun|pnpm|yarn)\s+publish\b/ },
];

function matchesReadOnlyBlocked(cmd: string): string | null {
  for (const { name, re } of READ_ONLY_BLOCKED_BASH) {
    if (re.test(cmd)) return name;
  }
  return null;
}

function handleReadOnlyToolAccess(toolName: string, input: Record<string, unknown>): ToolResult {
  if (PM_PLAN_FILE_TOOLS.has(toolName)) {
    if (isPlanFile(input)) {
      return { behavior: "allow", updatedInput: input };
    }
    return {
      behavior: "deny",
      message: "Discovery mode is read-only. File writes are restricted to plan files.",
    };
  }
  if (toolName === "Bash") {
    const cmd = String(input.command ?? "");
    const destructive = matchesDestructive(cmd);
    if (destructive) {
      return {
        behavior: "deny",
        message: `Destructive operation blocked (${destructive}). Use safer alternatives.`,
      };
    }
    const blocked = matchesReadOnlyBlocked(cmd);
    if (blocked) {
      return {
        behavior: "deny",
        message:
          `Planning mode does not run \`${blocked}\` — it is read-only. ` +
          `Every other command runs without asking for approval, so investigate freely. ` +
          `Finish the plan and call ExitPlanMode when the work should start.`,
      };
    }
  }
  return { behavior: "allow", updatedInput: input };
}

function handleBuildingToolAccess(toolName: string, input: Record<string, unknown>): ToolResult {
  if (toolName === "Bash") {
    const cmd = String(input.command ?? "");
    const matched = matchesDestructive(cmd);
    if (matched) {
      return {
        behavior: "deny",
        message: `Destructive operation blocked (${matched}). Use safer alternatives.`,
      };
    }
  }
  return { behavior: "allow", updatedInput: input };
}

function handleReviewToolAccess(toolName: string, input: Record<string, unknown>): ToolResult {
  // Review mode has full write access — reviewer can make direct fixes
  return handleBuildingToolAccess(toolName, input);
}

// Chat mode is a conversational assistant: full FILE write (so it can create
// files and attach them to the card) but its PR path is PLAN-GATED. Until the
// card has a saved plan it is a conversation, not a dev task, so every
// remote-pushing / PR-opening command is denied on top of the standard
// destructive guards. Saving a plan identifies the card, moves it to
// InProgress (maybeIdentifyOnFirstPlan, api) and opens these back up.
const CHAT_BLOCKED_BASH = /\bgit\s+push\b|\bgh\s+pr\b|\bhub\s+pull-request\b/;

/** Matches the PR tool prefixed or bare — names are unprefixed inside MCP. */
const CREATE_PR_TOOL = /(^|__)create_pull_request$/;

const CHAT_PLAN_GATE_MESSAGE = [
  "This chat card has no saved plan, so it has no PR path yet.",
  "If the conversation has turned into a development task, save a plan with update_task_plan first — that identifies the card and moves it to In Progress — then commit, push, and open the PR.",
  "If it is still a conversation, deliver the work by attaching files to the card with upload_attachment instead.",
].join(" ");

/** Is this the PR-opening path that a chat card must have a plan to use? */
function isChatPrTool(toolName: string, input: Record<string, unknown>): boolean {
  if (CREATE_PR_TOOL.test(toolName)) return true;
  return toolName === "Bash" && CHAT_BLOCKED_BASH.test(String(input.command ?? ""));
}

/**
 * Live-read the card's plan. Fails closed: a chat card whose plan cannot be
 * read stays conversational rather than gaining a PR path by accident.
 */
async function chatCardHasPlan(host: QueryHost): Promise<boolean> {
  try {
    const props = await host.connection.getTaskProperties();
    return !!props.plan?.trim();
  } catch {
    return false;
  }
}

function handleAutoToolAccess(
  toolName: string,
  input: Record<string, unknown>,
  hasExitedPlanMode: boolean,
  isParentTask: boolean,
): ToolResult {
  if (hasExitedPlanMode) {
    return isParentTask
      ? handleReviewToolAccess(toolName, input)
      : handleBuildingToolAccess(toolName, input);
  }
  // Pre-ExitPlanMode: trust the SDK's native plan mode for restrictions.
  // (Residual sessions only — auto now boots post-exit.)
  return { behavior: "allow", updatedInput: input };
}

function enforceMissingProps(
  host: QueryHost,
  input: Record<string, unknown>,
  missingProps: string[],
): ToolResult | null {
  if (missingProps.length === 0) return null;
  if (input.bypassValidation !== true) {
    return {
      behavior: "deny" as const,
      message: [
        "Cannot exit plan mode. Required task properties are missing:",
        ...missingProps.map((p) => `- ${p}`),
        "",
        "Fill these in using MCP tools (e.g. update_task_plan, update_task_properties), then call ExitPlanMode again.",
        "",
        "If you have a deliberate reason to proceed without them, you must explicitly bypass validation by calling ExitPlanMode with `bypassValidation: true` as a tool argument. Do not bypass unless the team has asked you to — it will be surfaced in chat.",
      ].join("\n"),
    };
  }
  host.connection.postChatMessage(
    `⚠️ [BYPASS] ExitPlanMode forced through with \`bypassValidation: true\` despite missing required properties: ${missingProps.join(", ")}. Please backfill these.`,
  );
  return null;
}

async function handleExitPlanMode(
  host: QueryHost,
  input: Record<string, unknown>,
): Promise<ToolResult> {
  if (host.hasExitedPlanMode) {
    return { behavior: "allow" as const, updatedInput: input };
  }

  try {
    const taskProps = await host.connection.getTaskProperties();
    const missingProps = collectMissingProps(taskProps);

    // Validate subtasks have plans if this is a parent task
    if (host.isParentTask) {
      try {
        const result = await host.connection.call("listSubtasks", {
          sessionId: host.connection.sessionId,
        });
        // No `view` in the payload → the server returns the full array
        // (the compact view is an object and carries no plan text anyway).
        const subtasks = Array.isArray(result) ? result : [];
        const subtasksWithoutPlans = subtasks.filter(
          (s: { plan?: string | null; title: string }) => !s.plan?.trim(),
        );
        if (subtasksWithoutPlans.length > 0) {
          const names = subtasksWithoutPlans.map((s: { title: string }) => s.title).join(", ");
          missingProps.push(
            `subtask plans — these subtasks are missing plans: ${names} (use update_subtask with plan field)`,
          );
        }
      } catch {
        // If we can't list subtasks, skip this validation
      }
    }

    const gate = enforceMissingProps(host, input, missingProps);
    if (gate) return gate;

    if (host.agentMode === "discovery") {
      // Discovery planning is complete. The card moves to Open (Identified)
      // and this session parks here for human review — it does NOT hand off
      // to auto or continue into building on its own. triggerIdentification
      // fires the identification pass but leaves agentMode/status alone
      // beyond that (see identification-methods.ts's Open-pause behavior).
      // hasExitedPlanMode stays false: this session never gains build access:
      // the human's explicit Build action (or a fresh session after it) is
      // what starts implementation.
      try {
        await host.connection.triggerIdentification();
      } catch (triggerErr) {
        host.connection.postChatMessage(
          `Identification trigger encountered an issue (${triggerErr instanceof Error ? triggerErr.message : "unknown error"}). Icon assignment may use fallbacks.`,
        );
      }
      await host.connection.postChatMessageAwait(
        "Plan posted and card moved to Open — waiting for review. Use Build (or switch modes) when you're ready to start implementation.",
      );
      host.discoveryCompleted = true;
      host.requestStop();
      return { behavior: "allow" as const, updatedInput: input };
    }

    // Auto mode: trigger identification but don't let it block ExitPlanMode.
    // Identification runs asynchronously server-side with fallbacks,
    // and the agent should proceed to building mode regardless.
    try {
      await host.connection.triggerIdentification();
    } catch (triggerErr) {
      host.connection.postChatMessage(
        `Identification trigger encountered an issue (${triggerErr instanceof Error ? triggerErr.message : "unknown error"}). Proceeding to build phase — identification will use fallbacks.`,
      );
    }

    host.hasExitedPlanMode = true;

    // Same-session continuation on BOTH harnesses: the SDK upgrades plan→build
    // permissions natively; under PTY the session presses Enter on the residual
    // plan dialog (planDialogAutoAccept) and continues in acceptEdits with the
    // settings allow-list covering the rest. The mode_changed event below is
    // deliberately unhandled server-side — DB task.agentMode must stay "auto"
    // so computeRunnerMode/canBypassPlanning resolve correctly on pod restarts.
    const newMode = host.isParentTask ? "review" : "building";
    host.connection.sendEvent({ type: "mode_transition", from: "auto", to: newMode });
    host.connection.emitModeChanged(newMode);

    return { behavior: "allow" as const, updatedInput: input };
  } catch (err) {
    return {
      behavior: "deny" as const,
      message: `Identification failed: ${err instanceof Error ? err.message : String(err)}. Fix the issue and try again.`,
    };
  }
}

async function handleAskUserQuestion(
  host: QueryHost,
  input: Record<string, unknown>,
): Promise<ToolResult> {
  const QUESTION_TIMEOUT_MS = 5 * 60 * 1000;
  const questions = input.questions as {
    question: string;
    header: string;
    options: { label: string; description: string; preview?: string }[];
    multiSelect?: boolean;
  }[];

  host.connection.emitStatus("waiting_for_input");
  host.connection.sendEvent({
    type: "tool_use",
    tool: "AskUserQuestion",
    input: JSON.stringify(input),
  });

  const answerPromise = host.connection.askUserQuestion(questions);
  const timeoutPromise = new Promise<null>((resolve) => {
    setTimeout(() => resolve(null), QUESTION_TIMEOUT_MS);
  });

  const answers = await Promise.race([answerPromise, timeoutPromise]);
  host.connection.emitStatus("running");

  if (!answers || Object.keys(answers).length === 0) {
    return {
      behavior: "deny",
      message:
        "User did not respond to clarifying questions in time. Proceed with your best judgment.",
    };
  }

  return { behavior: "allow", updatedInput: { questions: input.questions, answers } };
}

const DENIAL_WARNING_THRESHOLD = 3;
const DENIAL_FORCE_STOP_THRESHOLD = 8;

function handleDenialEscalation(host: QueryHost, consecutiveDenials: number): void {
  if (consecutiveDenials === DENIAL_WARNING_THRESHOLD) {
    host.connection.postChatMessage(
      `⚠️ Multiple tool denials detected. You are in ${host.agentMode} mode — ` +
        `file writes outside .claude/plans/ are not permitted. ` +
        `Focus on creating a plan instead of implementing code changes.`,
    );
  }
  if (consecutiveDenials >= DENIAL_FORCE_STOP_THRESHOLD) {
    host.connection.postChatMessage(
      `Agent force-stopped after ${DENIAL_FORCE_STOP_THRESHOLD} consecutive tool denials. ` +
        `The agent appears stuck — send a message to resume.`,
    );
    host.requestStop();
  }
}

/**
 * Break a run of identical tool calls. Returns a deny when the repeat must be
 * interrupted, null when the call may proceed.
 *
 * The tracker lives on the host, not on `buildCanUseTool`'s closure, because
 * that closure is rebuilt every turn (`buildQueryOptions`) — and the loop this
 * exists for spanned turns.
 */
function checkToolLoop(
  host: QueryHost,
  toolName: string,
  input: Record<string, unknown>,
): ToolResult | null {
  const fingerprint = fingerprintToolCall(toolName, input);
  if (!fingerprint) return null;
  const tracker = (host.toolLoop ??= new ToolLoopTracker());
  const verdict = tracker.record(fingerprint);
  if (verdict === "ok") return null;

  const repeats = tracker.repeatCount;
  if (verdict === "force_stop") {
    host.connection.postChatMessage(buildRepeatLoopChatMessage(repeats, true));
    host.requestStop();
    return {
      behavior: "deny",
      message: `Stopped after repeating this call ${repeats} times in a row.`,
    };
  }
  if (!tracker.alreadyReported) {
    tracker.markReported();
    host.connection.postChatMessage(buildRepeatLoopChatMessage(repeats, false));
  }
  return { behavior: "deny", message: buildRepeatLoopMessage(repeats, isHeavyGateActive()) };
}

function resolveToolAccess(
  host: QueryHost,
  toolName: string,
  input: Record<string, unknown>,
): ToolResult {
  switch (host.agentMode) {
    case "discovery":
    case "help":
      return handleReadOnlyToolAccess(toolName, input);
    case "building":
      return handleBuildingToolAccess(toolName, input);
    case "review":
      return handleReviewToolAccess(toolName, input);
    case "auto":
      return handleAutoToolAccess(toolName, input, host.hasExitedPlanMode, host.isParentTask);
    case "chat":
      // The chat-specific PR gate runs ahead of this in buildCanUseTool.
      return handleBuildingToolAccess(toolName, input);
    default:
      return { behavior: "allow", updatedInput: input };
  }
}

export function buildCanUseTool(
  host: QueryHost,
): (toolName: string, input: Record<string, unknown>) => Promise<ToolResult> {
  let consecutiveDenials = 0;
  // Cached once true — a plan never un-lands mid-session, so the gate costs at
  // most one round trip per PR attempt before it opens permanently.
  let chatHasPlan = false;

  return async (toolName, input) => {
    if (
      toolName === "ExitPlanMode" &&
      (host.agentMode === "auto" || host.agentMode === "discovery") &&
      !host.hasExitedPlanMode
    ) {
      return await handleExitPlanMode(host, input);
    }

    // Repeated ExitPlanMode calls after plan mode was already exited in discovery
    // mode should be denied to prevent burning budget without triggering force-stop.
    // (In practice unreachable in a single session — handleExitPlanMode's discovery
    // branch calls requestStop() — kept as defense-in-depth for a queued call racing
    // the stop.)
    if (toolName === "ExitPlanMode" && host.agentMode === "discovery" && host.hasExitedPlanMode) {
      return {
        behavior: "deny" as const,
        message: "Plan mode has already been exited.",
      };
    }

    if (toolName === "AskUserQuestion") {
      return await handleAskUserQuestion(host, input);
    }

    // Loop breaker runs before per-mode policy: a repeat is worth interrupting
    // whether or not the call would have been allowed.
    const loopResult = checkToolLoop(host, toolName, input);
    if (loopResult) return loopResult;

    // Chat mode's PR path is plan-gated: a plan-less chat card is still a
    // conversation, so pushing a branch or opening a PR is denied.
    if (host.agentMode === "chat" && isChatPrTool(toolName, input)) {
      if (!chatHasPlan) chatHasPlan = await chatCardHasPlan(host);
      if (!chatHasPlan) {
        consecutiveDenials++;
        handleDenialEscalation(host, consecutiveDenials);
        return { behavior: "deny" as const, message: CHAT_PLAN_GATE_MESSAGE };
      }
    }

    const result = resolveToolAccess(host, toolName, input);

    if (result.behavior === "deny") {
      consecutiveDenials++;
      handleDenialEscalation(host, consecutiveDenials);
    } else {
      consecutiveDenials = 0;
    }

    return result;
  };
}
